import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import EventIcon from "@mui/icons-material/Event";
import ClockIcon from "@mui/icons-material/HourglassBottom";
import PaidIcon from "@mui/icons-material/Paid";

export default function AdminDashboard() {
  const { profile } = useAuth();
  // Viewers can see who is waiting for approval but not approve/reject them.
  const readOnly = profile?.role === "viewer";
  const [pendingUsers, setPendingUsers] = useState([]);
  const [stats, setStats] = useState({
    tutors: 0,
    students: 0,
    sessions: 0,
    totalPayableHours: 0,
    totalPayableAmount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    user: null,
    action: "", // 'approved' or 'rejected'
  });

  const triggerConfirm = (user, action) => {
    setConfirmDialog({ open: true, user, action });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, user: null, action: "" });
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const firstDayOfMonth = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1,
      ).toISOString();

      // Fire all requests at once
      const [
        tutorsRes,
        studentsRes,
        sessionsRes,
        assignmentsRes,
        usedHoursRes,
        pendingRes,
      ] = await Promise.all([
        supabase.from("tutors").select("*", { count: "exact", head: true }),
        supabase.from("students").select("*", { count: "exact", head: true }),
        supabase
          .from("lesson_logs")
          .select("*", { count: "exact", head: true })
          .gte("date", firstDayOfMonth),
        supabase
          .from("student_tutor_assignments")
          // student_id is part of the usedHoursMap key below - without it
          // every key was `${tutor_id}_undefined`, never matched, and used
          // hours silently counted as 0 in both totals on this dashboard.
          .select("tutor_id, student_id, rate, hours_bought"),
        supabase
          .from("lesson_logs")
          .select("student_id, tutor_id, hours")
          .in("status", ["pending", "approved"]),
        supabase
          .from("profiles")
          .select("id, full_name, role")
          .eq("status", "pending")
          .order("created_at", { ascending: false }),
      ]);

      // Pre-calculate total used hours by a unique key (tutor_student)
      const usedHoursMap = usedHoursRes.data.reduce((acc, s) => {
        const key = `${s.tutor_id}_${s.student_id}`;
        acc[key] = (acc[key] || 0) + Number(s.hours);
        return acc;
      }, {});

      const totalPayableAmount = assignmentsRes.data.reduce((acc, asg) => {
        const key = `${asg.tutor_id}_${asg.student_id}`;
        const used = usedHoursMap[key] || 0;
        const remaining = Math.max(0, Number(asg.hours_bought) - used);
        return acc + remaining * asg.rate;
      }, 0);

      const totalPayableHours =
        assignmentsRes.data.reduce(
          (acc, asg) => acc + Number(asg.hours_bought),
          0,
        ) -
        assignmentsRes.data.reduce((acc, asg) => {
          const key = `${asg.tutor_id}_${asg.student_id}`;
          return acc + (usedHoursMap[key] || 0);
        }, 0);

      setStats({
        tutors: tutorsRes.count - 1 || 0,
        students: studentsRes.count || 0,
        sessions: sessionsRes.count || 0,
        totalPayableHours: totalPayableHours,
        totalPayableAmount: totalPayableAmount,
      });
      setPendingUsers(pendingRes.data || []);
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleStatusUpdate = async (userId, newStatus) => {
    setActionLoading(userId);
    const { error } = await supabase
      .from("profiles")
      .update({ status: newStatus })
      .eq("id", userId);

    if (!error) {
      loadDashboardData();
    } else {
      console.error("Error updating status:", error);
    }
    setActionLoading(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={20}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        color="primary"
        sx={{ mb: 4, fontWeight: "bold" }}
      >
        Admin Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* TUTORS OVERVIEW CARD */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderLeft: "6px solid",
              borderColor: "primary.main",
            }}
          >
            <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {stats.tutors}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Tutors
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* STUDENTS OVERVIEW CARD */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderLeft: "6px solid",
              borderColor: "secondary.main",
            }}
          >
            <SchoolIcon color="secondary" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {stats.students}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enrolled Students
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* SESSIONS OVERVIEW CARD */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderLeft: "6px solid",
              borderColor: "rgb(156, 94, 0)",
            }}
          >
            <EventIcon sx={{ fontSize: 40, color: "rgb(156, 94, 0)" }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {stats.sessions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sessions This Month
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* MAX PAID HOURS CARD */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderLeft: "6px solid",
              borderColor: "error.dark",
            }}
          >
            <ClockIcon
              color="error"
              sx={{ color: "error.dark", fontSize: 40 }}
            />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {stats.totalPayableHours}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ wordBreak: "break-word" }}
              >
                Max Payable Hours
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* MAX PAYABLE AMOUNT CARD */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderLeft: "6px solid",
              borderColor: "success.main",
            }}
          >
            <PaidIcon color="success" sx={{ fontSize: 40 }} />
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", wordBreak: "break-word" }}
              >
                R {stats.totalPayableAmount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Max Payable Amount
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* 2. PENDING APPROVALS*/}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Pending Approvals ({pendingUsers.length})
        </Typography>
        {pendingUsers.length === 0 ? (
          <Typography color="text.secondary">
            No new users waiting for approval.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                {!readOnly && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingUsers.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.full_name}</TableCell>
                  <TableCell>
                    <Chip label={p.role} size="small" variant="outlined" />
                  </TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          disabled={actionLoading === p.id}
                          onClick={() => triggerConfirm(p, "rejected")} // Trigger Dialog
                        >
                          {actionLoading === p.id ? (
                            <Typography variant="body2">Processing...</Typography>
                          ) : (
                            "Reject"
                          )}
                        </Button>

                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          disabled={actionLoading === p.id}
                          onClick={() => triggerConfirm(p, "approved")} // Trigger Dialog
                        >
                          {actionLoading === p.id ? (
                            <Typography variant="body2">Processing...</Typography>
                          ) : (
                            "Approve"
                          )}
                        </Button>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={confirmDialog.open} onClose={closeConfirm}>
        {confirmDialog.user !== null ? (
          <>
            <DialogTitle>
              {confirmDialog.action === "approved"
                ? "Confirm Approval"
                : "Confirm Rejection"}
            </DialogTitle>
            <DialogContent>
              <Typography>
                {confirmDialog.action === "approved"
                  ? `Are you sure you want to APPROVE `
                  : `Are you sure you want to REJECT `}
                <strong>{confirmDialog.user?.full_name}</strong>?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={closeConfirm} color="inherit">
                Cancel
              </Button>
              <Button
                variant="contained"
                color={
                  confirmDialog.action === "approved" ? "success" : "error"
                }
                onClick={() => {
                  handleStatusUpdate(
                    confirmDialog.user.id,
                    confirmDialog.action,
                  );
                  closeConfirm();
                }}
              >
                Confirm{" "}
                {confirmDialog.action === "approved" ? "Approve" : "Reject"}
              </Button>
            </DialogActions>
          </>
        ) : (
          <></>
        )}
      </Dialog>
    </Container>
  );
}
